import * as React from "react";
import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type MouseEvent,
  type ComponentProps,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Box,
  TextField,
  Dialog,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import SnackbarCustom, {
  type SnackbarData,
} from "../../components/SnackBarCustom";
import PopoverCustom from "../../components/PopoverCustom";
import { formatDate } from "../../utils/deteUtils";
import { getSites, updateSite } from "../../api";
import type { SiteItem, GetSitesParams } from "../../types/site";

type SiteListItem = SiteItem & {
  m_branch?: Record<string, string>;
  m_customer?: Record<string, string>;
  m_sale_organization?: Record<string, string>;
};

type SsInfoSearchContents = {
  display_code: string | null;
  display_code_disabled: boolean;
  name: string | null;
  pref: string[] | null;
  division_code: string[] | null;
  m_branch_id: number[] | null;
  m_customer_id: number[] | null;
  sir_at: string | null;
  owned_kind: number[] | null;
  sir_status: unknown | null;
  latest_alive?: unknown;
  latest_sales?: unknown;
  latest_authori?: unknown;
};

type SearchResultProps = {
  searchContent: SsInfoSearchContents;
};

type SiteColumnId =
  | "display_code"
  | "name"
  | "relation_code"
  | "sales_staff"
  | "operator"
  | "zip"
  | "address"
  | "owned_kind_name"
  | "compe_kind_name"
  | "tel"
  | "holiday"
  | "update_at"
  | "ss_import_at"
  | "location_import_at";

type SiteColumn = {
  id: SiteColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  bg?: boolean;
  isSmallScreen: boolean;
  textField?: boolean;
  m_branch?: boolean;
  m_sale_organization?: boolean;
  m_customer?: boolean;
};

const columns: SiteColumn[] = [
  {
    id: "display_code",
    label: "Sコード",
    minWidth: "112px",
    bg: true,
    isSmallScreen: true,
  },
  {
    id: "name",
    label: "SS名称",
    minWidth: "112px",
    bg: true,
    isSmallScreen: true,
  },
  { id: "relation_code", label: "納入先コード", minWidth: "120px", isSmallScreen: false },
  {
    id: "name",
    label: "支店",
    minWidth: "120px",
    align: "left",
    isSmallScreen: true,
    m_branch: true,
  },
  {
    id: "sales_staff",
    label: "担当者名",
    minWidth: "184px",
    align: "left",
    isSmallScreen: false,
    textField: true,
  },
  {
    id: "name",
    label: "得意先名",
    minWidth: "120px",
    align: "left",
    isSmallScreen: false,
    m_customer: true,
  },
  {
    id: "operator",
    label: "運営店",
    minWidth: "120px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "zip",
    label: "郵便番号",
    minWidth: "96px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "address",
    label: "住所",
    minWidth: "240px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "owned_kind_name",
    label: "所有区分",
    minWidth: "96px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "compe_kind_name",
    label: "運営区分",
    minWidth: "96px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "tel",
    label: "電話番号",
    minWidth: "120px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "holiday",
    label: "定休日",
    minWidth: "240px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "update_at",
    label: "更新日時",
    minWidth: "240px",
    align: "left",
    isSmallScreen: false,
  },
];

type OperatorTextfieldProps = {
  siteData: SiteListItem;
  changedValue: (value: string) => void;
};

const OperatorTextfield = ({ siteData, changedValue }: OperatorTextfieldProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (typeof changedValue === "function") {
      changedValue(e.target.value);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <TextField
        name="salesStaffTextfield"
        size="small"
        variant="outlined"
        value={siteData?.sales_staff || ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ pointerEvents: "auto" }}
      />
    </Box>
  );
};

const SearchResult = ({ searchContent }: SearchResultProps) => {

  //SS基本台帳インポート画面へ遷移
  const navigate = useNavigate();
  const handleClick = (path: string) => {
    navigate(path);
  };

  //詳細ポップアップ管理
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SiteListItem | null>(null);

  const handleLocationCodeClick = (row: SiteListItem, rowNum: number) => {
    setSelectedRow(row);
    setSelectedRowNum(rowNum);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };
  //検索結果ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 災害対象SS一覧取得
  const [sites, setSites] = useState<SiteListItem[]>([]);
  const [totalData, setTotalData] = useState<number | undefined>();

  const lastSearchedContentRef = useRef<SsInfoSearchContents | null>(null);

  const fetchSites = async (overridePage?: number) => {
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchContent
        : (lastSearchedContentRef.current ?? searchContent);
    try {
      const response = await getSites({
        display_code: contentToUse.display_code,
        name: contentToUse.name,
        pref: contentToUse.pref as unknown as GetSitesParams["pref"],
        m_branch_id: contentToUse.m_branch_id ?? undefined,
        m_customer_id: contentToUse.m_customer_id ?? undefined,
        latest_alive: contentToUse.latest_alive,
        latest_sales: contentToUse.latest_sales,
        latest_authori: contentToUse.latest_authori,
        owned_kind: contentToUse.owned_kind ?? undefined,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status === "ok") {
        const data = response.sites;
        const total = response.total_count;
        const status: SnackbarData = response;
        console.log("status", status);
        setSites(data);
        setTotalData(total);
        if (overridePage !== undefined) {
          setPage(overridePage);
          lastSearchedContentRef.current = searchContent;
        } else {
          lastSearchedContentRef.current = contentToUse;
        }
        setSnackBarstatus(status);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);
  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
    } else {
      fetchSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  //担当者名管理
  const handleChangeOperator = (inputValue: string, siteId: number) => {
    const updatedArray = [...sites]; // オリジナルの配列をコピー
    //sites配列内からidが当てはまる配列を探す
    const updatedItem = updatedArray.find((item) => item.id === siteId);
    if (updatedItem) {
      updatedItem.sales_staff = inputValue;
      setSites(updatedArray);
    }
  };

  //登録ボタン選択行管理
  const [selectedRowNum, setSelectedRowNum] = useState<number>(0);

  //担当者名登録ポップオーバー管理
  const [confirmPopoverOpen, setPopoverSplitOpen] = useState(false);
  const putPostButtonClick = (e: MouseEvent, rowNum: number) => {
    e.stopPropagation();
    setSelectedRowNum(rowNum);
    setPopoverSplitOpen(true);
  };
  const confirmPopupClose = () => {
    setPopoverSplitOpen(false);
  };

  //担当者名登録
  const putOperator = async (isSuccess: boolean) => {
    if (!isSuccess) {
      return;
    }

    const params = {
      key: "sales_staff",
      sales_staff: String(sites[selectedRowNum].sales_staff ?? ""),
      // sir_start_at: "2024/01/17",
    };
    try {
      const status = await updateSite(sites[selectedRowNum].id, params);
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  type ButtonVaridateProps = { rowNum: number };

  const ButtonVaridate = ({ rowNum }: ButtonVaridateProps) => {
    return (
      <IconButton
        name="putButton"
        color="primary"
        sx={{ pointerEvents: "auto" }}
        onClick={(e) => putPostButtonClick(e, rowNum)}
      >
        <SaveIcon />
      </IconButton>
    );
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData | undefined>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          padding: "16px 0 32px",
          "@media screen and (max-width:600px)": {
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <Button
          name="searchButton"
          variant="contained"
          onClick={() => {
            setPage(0);
            fetchSites(0);
          }}
          sx={{ width: "208px" }}
        >
          検索
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleClick("/ssinfo/import")}
          sx={{ width: "208px" }}
        >
          <DownloadIcon />
          SS基本台帳インポート
        </Button>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0px",
              },
            }}
          >
            検索結果
          </Box>
        </Box>
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns?.map((column) => (
                    <TableCell
                      key={column.label}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        padding: "8px",
                        fontWeight: "bold",
                        borderBottom: 2,
                        borderBottomColor: "outline.dark",
                        backgroundColor: column.bg ? "primary.light" : "#FFF",
                        color: column.bg ? "#FFF" : "outline.dark",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sites?.map((site, rowNum) => {
                  return (
                    <TableRow
                      {...({
                        name: `searchResult_${rowNum}`,
                      } as ComponentProps<typeof TableRow>)}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={site.id}
                      onClick={() => handleLocationCodeClick(site, rowNum)}
                      sx={{
                        pointerEvents: "none",
                        "@media screen and (max-width:900px)": {
                          cursor: "pointer",
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      {columns.map((column) => {
                        return (
                          <TableCell
                            key={column.label}
                            align={column.align}
                            sx={{
                              padding: "8px",
                              "@media screen and (max-width:900px)": {
                                display:
                                  column.isSmallScreen === true
                                    ? "table-cell"
                                    : "none",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "32px",
                              }}
                            >
                              {column.textField ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <OperatorTextfield
                                    siteData={site}
                                    changedValue={(value) =>
                                      // handleChangeOperator(value, rowNum)
                                      handleChangeOperator(value, site.id)
                                    }
                                  />
                                  <ButtonVaridate rowNum={rowNum} />
                                </Box>
                              ) : column.m_branch ? (
                                <>{site.m_branch?.[column.id]}</>
                              ) : column.m_sale_organization ? (
                                <>{site.m_sale_organization?.[column.id]}</>
                              ) : column.m_customer ? (
                                <>{site.m_customer?.[column.id]}</>
                              ) : column.id === "ss_import_at" ||
                                column.id === "location_import_at" ||
                                column.id === "update_at" ? (
                                formatDate(String(site[column.id] ?? ""))
                              ) : (
                                <>{site?.[column.id]}</>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog
            open={isPopoverOpen}
            onClose={handlePopoverClose}
            className="shogoshogo"
          >
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "4px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                基本情報詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.map((column) => {
                    return (
                      <TableRow
                        role="checkbox"
                        tabIndex={-1}
                        key={column.label}
                      >
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                            overflowWrap: "break-word",
                          }}
                        >
                          {column.textField ? (
                            <Box sx={{ display: "flex" }}>
                              <OperatorTextfield
                                siteData={sites[selectedRowNum]}
                                changedValue={(value) =>
                                  // handleChangeOperator(value, selectedRowNum)
                                  handleChangeOperator(value, selectedRow.id)
                                }
                              />
                              <ButtonVaridate rowNum={selectedRowNum} />
                            </Box>
                          ) : column.m_branch ? (
                            <>{selectedRow.m_branch?.[column.id]}</>
                          ) : column.m_sale_organization ? (
                            <>{selectedRow.m_sale_organization?.[column.id]}</>
                          ) : column.m_customer ? (
                            <>{selectedRow.m_customer?.[column.id]}</>
                          ) : (
                            <>{selectedRow[column.id]}</>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Dialog>
          <TablePagination
            rowsPerPageOptions={[10, 30, 50, 100]}
            component="div"
            count={totalData ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
      <PopoverCustom
        open={confirmPopoverOpen}
        onClose={confirmPopupClose}
        yesNo={(e) => putOperator(e)}
        title={"登録確認"}
        contents={"担当者名を登録してよろしいですか？"}
      />
    </>
  );
};
export default SearchResult;
